import React, {useState, useEffect} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from "@material-ui/core";
import {Refresh as RefreshIcon, Save as SaveIcon} from "@material-ui/icons";
import {toast} from "react-toastify";
import axios from "axios";

export default function CertificatePreview({open, onClose, workshop, settings}) {
  const [previewSettings, setPreviewSettings] = useState({
    name_x: 600,
    name_y: 400,
    font_size: 48,
    font_color: "#000000",
  });
  const [sampleName, setSampleName] = useState("John Doe");
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const imgRef = React.useRef(null);
  const [imgSize, setImgSize] = useState({width: 0, height: 0});

  const API_URL = window?.location?.hostname?.includes("maroc")
    ? process.env.REACT_APP_API_MAROC
    : process.env.REACT_APP_API;

  useEffect(() => {
    if (settings) {
      setPreviewSettings(settings);
    }
  }, [settings]);

  useEffect(() => {
    // Set a sample name from the first participant if available
    if (workshop?.participants && workshop.participants.length > 0) {
      const firstParticipant = workshop.participants[0];
      setSampleName(`${firstParticipant.fname} ${firstParticipant.lname}`);
    }
  }, [workshop]);

  useEffect(() => {
    // Check if settings have changed
    if (settings) {
      const changed =
        previewSettings.name_x !== settings.name_x ||
        previewSettings.name_y !== settings.name_y ||
        previewSettings.font_size !== settings.font_size ||
        previewSettings.font_color !== settings.font_color;
      setHasChanges(changed);
    }
  }, [previewSettings, settings]);

  const ATT_API_URL = window?.location?.hostname?.includes("maroc")
    ? process.env.REACT_APP_FILES_PATH_MAROC
    : process.env.REACT_APP_FILES_PATH;

  const certificateUrl = workshop?.certificate_template
    ? `${ATT_API_URL}/certificates/${workshop.certificate_template}`
    : null;
  const handleSaveSettings = async () => {
    if (!workshop?.id) {
      toast.error("Workshop ID not found");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/workshops/${workshop.id}/settings`, previewSettings);
      toast.success("Certificate settings saved successfully");
      setHasChanges(false);

      // Close dialog and refresh parent
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setPreviewSettings(settings);
      setHasChanges(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" style={{fontWeight: 600}}>
              Certificate Preview
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Adjust the position and styling to see how the certificate will look
            </Typography>
          </Box>
          {hasChanges && (
            <Box px={2} py={0.5} borderRadius={4} bgcolor="#fef3c7" border="1px solid #f59e0b">
              <Typography variant="caption" style={{color: "#d97706", fontWeight: 600}}>
                Unsaved Changes
              </Typography>
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Controls */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} style={{padding: 16, backgroundColor: "#f7fafc"}}>
              <Typography variant="subtitle2" gutterBottom style={{fontWeight: 600}}>
                Preview Controls
              </Typography>

              <Box mt={2}>
                <TextField
                  label="Sample Name"
                  fullWidth
                  value={sampleName}
                  onChange={(e) => setSampleName(e.target.value)}
                  variant="outlined"
                  size="small"
                  margin="dense"
                />
              </Box>

              <Box mt={2}>
                <Typography variant="caption" display="block" gutterBottom>
                  Horizontal Position (X): {previewSettings.name_x}px
                </Typography>
                <Slider
                  value={previewSettings.name_x}
                  onChange={(e, val) => setPreviewSettings({...previewSettings, name_x: val})}
                  min={0}
                  max={3000}
                  step={10}
                  valueLabelDisplay="auto"
                  color="secondary"
                />
              </Box>

              <Box mt={2}>
                <Typography variant="caption" display="block" gutterBottom>
                  Vertical Position (Y): {previewSettings.name_y}px
                </Typography>
                <Slider
                  value={previewSettings.name_y}
                  onChange={(e, val) => setPreviewSettings({...previewSettings, name_y: val})}
                  min={0}
                  max={3000}
                  step={10}
                  valueLabelDisplay="auto"
                  color="secondary"
                />
              </Box>

              <Box mt={2}>
                <Typography variant="caption" display="block" gutterBottom>
                  Font Size: {previewSettings.font_size}px
                </Typography>
                <Slider
                  value={previewSettings.font_size}
                  onChange={(e, val) => setPreviewSettings({...previewSettings, font_size: val})}
                  min={12}
                  max={120}
                  step={2}
                  valueLabelDisplay="auto"
                  color="secondary"
                />
              </Box>

              <Box mt={2}>
                <Typography variant="caption" display="block" gutterBottom>
                  Font Color
                </Typography>
                <TextField
                  type="color"
                  fullWidth
                  value={previewSettings.font_color}
                  onChange={(e) =>
                    setPreviewSettings({
                      ...previewSettings,
                      font_color: e.target.value,
                    })
                  }
                  variant="outlined"
                  size="small"
                />
              </Box>

              {workshop?.participants && workshop.participants.length > 0 && (
                <Box mt={2}>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Select Participant</InputLabel>
                    <Select
                      value={sampleName}
                      onChange={(e) => setSampleName(e.target.value)}
                      label="Select Participant"
                    >
                      {workshop.participants.map((p) => (
                        <MenuItem key={p.id} value={`${p.prefix ? p.prefix + " " : ""}${p.fname} ${p.lname}`}>
                          {p.prefix && `${p.prefix} `}
                          {p.fname} {p.lname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Box mt={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={!hasChanges}
                  style={{textTransform: "none"}}
                >
                  Reset to Saved Settings
                </Button>
              </Box>

              {hasChanges && (
                <Box mt={2}>
                  <Paper
                    elevation={0}
                    style={{
                      padding: 12,
                      backgroundColor: "#fef3c7",
                      border: "1px solid #f59e0b",
                    }}
                  >
                    <Typography variant="caption" style={{color: "#d97706"}}>
                      ⚠️ You have unsaved changes. Click "Save Settings" to apply them.
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Preview */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={3}
              style={{
                position: "relative",
                overflow: "hidden",
                backgroundColor: "#e5e7eb",
                minHeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {certificateUrl ? (
                <Box
                  style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 800,
                  }}
                >
                  <img
                    onLoad={(e) => {
                      setImgSize({
                        width: e.target.naturalWidth,
                        height: e.target.naturalHeight,
                      });
                    }}
                    src={certificateUrl}
                    alt="Certificate template"
                    style={{
                      width: "100%",
                      display: "block",
                      border: "1px solid #cbd5e0",
                    }}
                  />
                  <Box
                    style={{
                      position: "absolute",
                      left: `${(previewSettings.name_x / imgSize?.width) * 100}%`,
                      top: `${(previewSettings.name_y / imgSize?.height) * 100}%`,
                      transform: "translate(0%, -100%)",
                      pointerEvents: "none",
                    }}
                  >
                    <span
                      style={{
                        fontSize: `${previewSettings.font_size / 2.5}px`,
                        color: previewSettings.font_color,
                        fontWeight: 600,
                        textAlign: "center",
                        textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sampleName}
                    </span>
                  </Box>
                </Box>
              ) : (
                <Box textAlign="center" p={4}>
                  <Typography variant="h6" color="textSecondary">
                    No certificate template uploaded
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Upload a certificate template to preview
                  </Typography>
                </Box>
              )}
            </Paper>

            <Box mt={2}>
              <Paper elevation={0} style={{padding: 12, backgroundColor: "#dbeafe"}}>
                <Typography variant="caption" style={{fontWeight: 500}}>
                  💡 <strong>Tip:</strong> The preview is scaled down. Actual certificate will use the exact
                  pixel positions you set. Current settings: X={previewSettings.name_x}px, Y=
                  {previewSettings.name_y}px, Size={previewSettings.font_size}px
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{padding: 16, backgroundColor: "#f7fafc"}}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button onClick={onClose} style={{textTransform: "none"}} disabled={saving}>
            Close
          </Button>
          <Box display="flex" gap={1}>
            {hasChanges && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleReset}
                disabled={saving}
                style={{textTransform: "none"}}
              >
                Discard Changes
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSaveSettings}
              disabled={saving || !hasChanges}
              startIcon={saving ? null : <SaveIcon />}
              style={{textTransform: "none", fontWeight: 600}}
            >
              {saving ? "Saving..." : hasChanges ? "Save Settings" : "Settings Saved"}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
